import { useQuery } from '@tanstack/react-query';

import Group from 'models/Group';
import { GroupAccessMode, groupsQueryKeys, indexGroups } from 'services/api/group';

// Stable identity for the unresolved state — avoids creating a new [] on every render,
// which would invalidate downstream memos and effects that depend on the result. Typed as the
// resolved element type (Group) so consumers get the full facility shape, not just {id, name}.
const EMPTY_FACILITIES: Group[] = [];

// The buildings the current user may address a referral to. Every surface that OFFERS a
// facility passes a specific `type` (one provider type); passing nothing yields every
// provider type the user operates, which only the read-only name lookups want — an id
// attached before the scope existed can still be named. The resolved params are folded into
// the query key, so each scope is its own cache entry rather than colliding.
export function useAllowedFacilities({ type }: { type?: string } = {}) {
  const params = {
    accessModes: [GroupAccessMode.Allowed],
    pageSize: 200,
    sortBy: 'name',
    ...(type ? { type } : { isProvider: true }),
  };

  const query = useQuery({
    queryKey: [...groupsQueryKeys.index, params],
    queryFn: ({ signal }) => indexGroups(params, signal),
  });

  // `isComplete` is the only state in which an id missing from `facilities` is known not to be
  // allowed: a failed request is also not pending, a user past the page size has more, and a cached
  // list being refetched can be another acting client's — the key does not carry the client, and
  // not every client switch clears the cache.
  const isComplete = query.isSuccess && !query.isFetching && !query.data.links?.next;

  return { facilities: query.data?.data ?? EMPTY_FACILITIES, isPending: query.isPending, isComplete };
}
